// Vite SPA entry: StrictMode → ThemeProvider → MotionConfig → App.
//
// MotionConfig reducedMotion="user" makes every motion component honour
// prefers-reduced-motion. SkipLink is the first focusable element (Tab →
// "Skip to main content" → <main id="main">); LiveRegion is the single
// aria-live region useLiveAnnounce() writes to.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MotionConfig } from 'motion/react';
import { App } from './App';
import { ThemeProvider } from './theme/ThemeProvider';
import { SkipLink, LiveRegion } from './lib/a11y';
import { captureException, initObservability } from './lib/observability';
import { router } from './router';
import './design-tokens/tokens.css';
import './design-tokens/fonts.css';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Studio UI: missing #root element in index.html');

// Sentry + PostHog: no-ops unless VITE_SENTRY_DSN / VITE_POSTHOG_KEY were set at
// build time; the SDKs are lazy-loaded so the entry chunk never pays for them.
void initObservability({ router });

// React 19 no longer re-throws boundary-caught errors to window.onerror, so the
// root's uncaught/recoverable errors go through the façade explicitly.
createRoot(rootEl, {
  onUncaughtError: (error, info) =>
    captureException(error, { componentStack: info.componentStack, tags: { boundary: 'root' } }),
  onRecoverableError: (error, info) =>
    captureException(error, {
      componentStack: info.componentStack,
      tags: { boundary: 'recoverable' },
    }),
}).render(
  <StrictMode>
    <ThemeProvider>
      <MotionConfig reducedMotion="user">
        <SkipLink />
        <LiveRegion />
        <App />
      </MotionConfig>
    </ThemeProvider>
  </StrictMode>,
);
